import React from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTheme } from '../context/ThemeContext';
import { 
  FaArrowLeft as FaArrowLeftBase, 
  FaExclamationTriangle as FaExclamationTriangleBase 
} from 'react-icons/fa';

// Fix for TS2786
const FaArrowLeft = FaArrowLeftBase as React.ElementType;
const FaExclamationTriangle = FaExclamationTriangleBase as React.ElementType;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const StockMarketTracker: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const data = {
    labels: ['10:00', '11:00', '12:00', '13:00', '14:00', '15:00'],
    datasets: [
      {
        label: 'AAPL Price',
        data: [150, 152, 149, 153, 155, 154],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.1,
        pointRadius: 2,
      },
      {
        label: 'LSTM Prediction',
        data: [150, 151, 150, 152, 156, 158],
        borderColor: '#6366f1',
        borderDash: [5, 5],
        tension: 0.1,
        pointRadius: 0,
      }
    ],
  };

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/portfolio" className="inline-flex items-center text-secondary hover:text-accent mb-8 transition-colors">
          <FaArrowLeft className="mr-2" /> Back to Portfolio
        </Link>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-2">Algorithmic Trading Dashboard</h1>
            <p className="text-secondary">Real-time ticker visualization with LSTM-based price forecasting.</p>
          </div>
          <div className="mt-4 md:mt-0 flex gap-2">
            <span className="px-3 py-1 bg-green-900/30 text-green-500 border border-green-500/30 rounded text-sm">Market Open</span>
            <span className="px-3 py-1 bg-blue-900/30 text-blue-500 border border-blue-500/30 rounded text-sm">Latency: 24ms</span>
          </div>
        </div>

        {/* Trading Terminal UI */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 shadow-2xl mb-12 text-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 border-b border-gray-800 pb-6">
            <div>
              <p className="text-gray-500 text-xs uppercase">Symbol</p>
              <p className="text-2xl font-mono font-bold">AAPL</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Current Price</p>
              <p className="text-2xl font-mono font-bold text-green-400">$154.20</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">24h Change</p>
              <p className="text-2xl font-mono font-bold text-green-400">+2.4%</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Model Confidence</p>
              <p className="text-2xl font-mono font-bold text-blue-400">87.5%</p>
            </div>
          </div>

          <div className="h-96 w-full">
            <Line 
              data={data} 
              options={{ 
                responsive: true, 
                maintainAspectRatio: false,
                scales: {
                  x: { grid: { color: '#374151' }, ticks: { color: '#9ca3af' } },
                  y: { grid: { color: '#374151' }, ticks: { color: '#9ca3af' } }
                },
                plugins: { legend: { labels: { color: '#e5e7eb' } } }
              }} 
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-primary mb-4">The Model</h3>
            <p className="text-secondary mb-4">
              This project utilizes a Long Short-Term Memory (LSTM) recurrent neural network trained on 5 years of historical OHLCV data. 
              The model is served via a FastAPI microservice and updated daily.
            </p>
            <div className="bg-primary p-4 rounded-lg border border-primary flex items-start gap-3">
              <FaExclamationTriangle className="text-yellow-500 mt-1" />
              <p className="text-sm text-secondary">
                <strong>Disclaimer:</strong> This is a portfolio project demonstrating data engineering and ML capabilities. It is not financial advice.
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-primary mb-4">Architecture</h3>
            <ul className="space-y-4">
              <li className="flex items-center justify-between p-3 bg-primary rounded border border-primary">
                <span className="text-primary font-medium">Data Source</span>
                <span className="text-secondary text-sm">Yahoo Finance API</span>
              </li>
              <li className="flex items-center justify-between p-3 bg-primary rounded border border-primary">
                <span className="text-primary font-medium">Processing</span>
                <span className="text-secondary text-sm">Pandas / NumPy</span>
              </li>
              <li className="flex items-center justify-between p-3 bg-primary rounded border border-primary">
                <span className="text-primary font-medium">ML Framework</span>
                <span className="text-secondary text-sm">TensorFlow Keras</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockMarketTracker;